/**
 * StudySync Header Component
 * Navigation header with authentication and branding
 */

import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from "@mui/material";
import {
  AccountCircle,
  ExitToApp,
  Dashboard,
  Assessment,
  ShowChart,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSecureAuth } from "../../contexts/SecureAuthContext";
import { useUser } from "../../contexts/UserContext";
import { analyticsService } from "../../services/analyticsService";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, logout } = useSecureAuth();
  const { isMasterAccount, isApprovedResearcher } = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      analyticsService.trackSignOut();
      analyticsService.resetUser();
      await logout();
      navigate("/");
      handleMenuClose();
    } catch (error) {}
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
      <Toolbar>
        {/* Logo and Brand */}
        <Box
          component="img"
          src="/finalized_logo/Study Sync Short Logo.svg"
          alt="StudySync"
          sx={{
            height: { xs: 32, sm: 36 },
            cursor: 'pointer',
            mr: 4,
          }}
          onClick={() => navigate("/")}
        />

        {/* Navigation Links */}
        <Box sx={{ flexGrow: 1, display: "flex", gap: 2 }}>
          {currentUser && (
            <>
              {isMasterAccount && (
                <Button
                  color="inherit"
                  startIcon={<Dashboard sx={{ color: "primary.main" }} />}
                  onClick={() => navigate("/master/dashboard")}
                  sx={{
                    fontWeight: isActive("/master/dashboard") ? 600 : 400,
                    textDecoration: isActive("/master/dashboard")
                      ? "underline"
                      : "none",
                    color: "primary.main",
                  }}
                >
                  Master Dashboard
                </Button>
              )}
              {isApprovedResearcher && (
                <Button
                  color="inherit"
                  startIcon={<Dashboard sx={{ color: "primary.main" }} />}
                  onClick={() => navigate("/researcher/dashboard")}
                  sx={{
                    fontWeight: isActive("/researcher/dashboard") ? 600 : 400,
                    textDecoration: isActive("/researcher/dashboard")
                      ? "underline"
                      : "none",
                    color: "primary.main",
                  }}
                >
                  Dashboard
                </Button>
              )}
              {isMasterAccount && (
                <Button
                  color="inherit"
                  startIcon={<Assessment sx={{ color: "primary.main" }} />}
                  onClick={() => navigate("/study-management/analytics")}
                  sx={{
                    fontWeight: isActive("/study-management/analytics")
                      ? 600
                      : 400,
                    textDecoration: isActive("/study-management/analytics")
                      ? "underline"
                      : "none",
                    color: "primary.main",
                  }}
                >
                  Portfolio Overview
                </Button>
              )}
              {isMasterAccount && (
                <Button
                  color="inherit"
                  startIcon={<ShowChart sx={{ color: "primary.main" }} />}
                  onClick={() => navigate("/admin-adherence-monitor")}
                  sx={{
                    fontWeight: isActive("/adherence-monitor") ? 600 : 400,
                    textDecoration: isActive("/adherence-monitor")
                      ? "underline"
                      : "none",
                    color: "primary.main",
                  }}
                >
                  Adherence Monitor
                </Button>
              )}
            </>
          )}
        </Box>

        {/* User Menu or Auth Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {currentUser ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  display: { xs: "none", sm: "block" },
                  color: "text.primary",
                }}
              >
                Researcher {currentUser.displayName || currentUser.email}
              </Typography>
              <IconButton
                size="large"
                edge="end"
                aria-label="account menu"
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                sx={{ color: "primary.main" }}
              >
                {currentUser.photoURL ? (
                  <Avatar
                    src={currentUser.photoURL}
                    alt={currentUser.displayName || "User"}
                    sx={{ width: 32, height: 32 }}
                  />
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                {isMasterAccount && (
                  <MenuItem
                    onClick={() => handleNavigation("/master/dashboard")}
                  >
                    <Dashboard sx={{ mr: 1 }} />
                    Master Dashboard
                  </MenuItem>
                )}
                {isApprovedResearcher && (
                  <MenuItem
                    onClick={() => handleNavigation("/researcher/dashboard")}
                  >
                    <Dashboard sx={{ mr: 1 }} />
                    Dashboard
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ExitToApp sx={{ mr: 1 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={() => navigate("/participant/enroll")}
                sx={{
                  mr: 1,
                  backgroundColor: "#00505C",
                  color: "white",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#003540",
                  },
                }}
              >
                Join Study
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/researcher/auth")}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "primary.dark",
                    backgroundColor: "rgba(140, 21, 21, 0.04)",
                  },
                }}
              >
                Researcher Login
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
